import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";

import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import {
  formatOverviewValue,
  KPI_IDS,
  OVERVIEW_FUNNELS,
  OVERVIEW_STAGES,
  OVERVIEW_WINDOWS,
  overviewDates,
  overviewSearch,
  overviewTileSub,
  overviewUrlState,
  funnelRowsForStage,
  packQrScanners,
  type OverviewDays,
  type OverviewStage,
  type OverviewUrlState,
} from "./overview-format";
import { getCodes, qrCodesKey } from "./qr-api";
import {
  getFunnelOverview,
  statsOverviewKey,
  type OverviewCell,
  type OverviewFunnelKey,
  type OverviewFunnelRow,
  type OverviewTile,
} from "./stats-api";

const FUNNEL_SLUG = "precision-dosing";
const integer = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
const numeric = { meta: { align: "right" as const } };

const STAGE_FILTER: FilterSpec = {
  name: "ov_stage",
  label: "Stage",
  kind: "choice",
  options: OVERVIEW_STAGES.map(({ key, label }) => ({ value: key, label })),
};

function OverviewControls({
  state,
  onChange,
}: {
  state: OverviewUrlState;
  onChange: (next: OverviewUrlState) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-4">
      <label className="flex flex-col gap-1 text-sm text-ks-ink">
        Window
        <Select value={String(state.days)} onValueChange={(value) => onChange({ ...state, days: Number(value) as OverviewDays })}>
          <SelectTrigger data-nonmutating className="min-w-32" aria-label="Window">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OVERVIEW_WINDOWS.map((days) => <SelectItem key={days} value={String(days)}>{days} days</SelectItem>)}
          </SelectContent>
        </Select>
      </label>
      <label className="flex flex-col gap-1 text-sm text-ks-ink">
        Funnel
        <Select value={state.funnel} onValueChange={(value) => onChange({ ...state, funnel: value as OverviewFunnelKey })}>
          <SelectTrigger data-nonmutating className="min-w-52" aria-label="Funnel">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OVERVIEW_FUNNELS.map((funnel) => <SelectItem key={funnel.key} value={funnel.key}>{funnel.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </label>
      <FilterBar
        layout="segmented"
        filters={[STAGE_FILTER]}
        values={{ ov_stage: state.stage }}
        onChange={(values) => onChange({ ...state, stage: String(values.ov_stage ?? "both") as OverviewStage })}
      />
    </div>
  );
}

function LabelTooltip({ tile }: { tile: OverviewTile }) {
  const parts = tile.parts;
  return (
    <Tooltip>
      <TooltipTrigger asChild><span className="cursor-help" tabIndex={0}>{tile.label}</span></TooltipTrigger>
      <TooltipContent>
        <span>KPI: {KPI_IDS[tile.key]}</span>
        {parts && (
          <span>
            {` · Landing visitors ${parts.landing_visitors === null ? "Needs data" : integer.format(parts.landing_visitors)}`}
            {` · Client Solutions intake ${parts.cs_intake_starts === null ? "Needs data" : integer.format(parts.cs_intake_starts)}`}
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function TileBody({ tile, qrPending }: { tile: OverviewTile; qrPending: boolean }) {
  const pending = tile.from_qr_service === true && qrPending;
  const checkins = tile.key === "stickiness" ? tile.detail?.checkins : undefined;
  const subline = tile.from_qr_service && tile.value !== null
    ? "unique pack-QR scanners (each code and day counted once)"
    : overviewTileSub(tile);
  return (
    <Card size="sm" className="h-full">
      <CardHeader>
        <CardTitle className="text-sm text-ks-ink"><LabelTooltip tile={tile} /></CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-1">
        {pending ? <Skeleton className="h-9 w-28" /> : <p className="text-3xl font-semibold tabular-nums text-ks-ink">{formatOverviewValue(tile)}</p>}
        <p className="text-xs text-muted-foreground">{subline}</p>
        {checkins && (
          <p className="text-xs text-muted-foreground">
            Check-ins answered: {checkins.value === null ? "Needs data" : integer.format(checkins.value)}
            {checkins.needs_data?.reason ? ` · ${checkins.needs_data.reason}` : ""}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function TileCard({
  tile,
  qrPending,
  tab,
  route,
}: {
  tile: OverviewTile;
  qrPending: boolean;
  tab: string | null;
  route: { area: string; module: string };
}) {
  const body = <TileBody tile={tile} qrPending={qrPending} />;
  if (!tab) return body;
  return (
    <Link
      to="/app/$area/$module/$tab"
      params={{ ...route, tab }}
      search={(previous: Record<string, unknown>) => previous as never}
      data-nonmutating
      className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ks-purple"
    >
      {body}
    </Link>
  );
}

function tileTab(key: OverviewTile["key"]): string | null {
  if (key === "leads") return "funnels";
  if (["new_app_users", "lead_to_app", "results_given", "order_made"].includes(key)) return "journey";
  return null;
}

function TileSection({
  title,
  tiles,
  packScanners,
  qrFailed,
  qrPending,
  route,
}: {
  title: string;
  tiles: OverviewTile[] | undefined;
  packScanners: number | null;
  qrFailed: boolean;
  qrPending: boolean;
  route: { area: string; module: string };
}) {
  return (
    <section className="flex flex-col gap-3" aria-label={title}>
      <h2 className="text-sm font-bold tracking-wide text-ks-purple uppercase">{title}</h2>
      <TooltipProvider>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {tiles === undefined
            ? Array.from({ length: title === "Pre-purchase" ? 5 : 6 }, (_, index) => (
                <Card size="sm" key={index}><CardContent className="flex flex-col gap-3"><Skeleton className="h-4 w-28" /><Skeleton className="h-9 w-24" /><Skeleton className="h-3 w-full" /></CardContent></Card>
              ))
            : tiles.map((sourceTile) => {
                const tile = sourceTile.from_qr_service
                  ? {
                      ...sourceTile,
                      value: qrFailed ? null : packScanners,
                      needs_data: qrFailed ? { reason: "The QR service did not answer" } : sourceTile.needs_data,
                    }
                  : sourceTile;
                return <TileCard key={tile.key} tile={tile} qrPending={qrPending} tab={tileTab(tile.key)} route={route} />;
              })}
        </div>
      </TooltipProvider>
    </section>
  );
}

function NeedsValue({ text, reason }: { text: string; reason: string | null }) {
  if (!reason) return <>{text}</>;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild><span className="cursor-help underline decoration-dotted underline-offset-4" tabIndex={0}>{text}</span></TooltipTrigger>
        <TooltipContent>{reason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function cellValue(key: OverviewTile["key"], cell: OverviewCell, replacement?: { value: number | null; reason: string | null }): ReactNode {
  if (cell.not_applicable) return "already bought";
  const value = replacement ? replacement.value : cell.value;
  const reason = replacement ? replacement.reason : cell.needs_data?.reason ?? null;
  const formatted = formatOverviewValue({ key, value });
  return value === null ? <NeedsValue text={formatted} reason={reason} /> : formatted;
}

function Sparkline({ points }: { points: OverviewFunnelRow["leads_trend"] }) {
  if (!points) return <>—</>;
  const values = points.map((point) => point.value);
  const max = Math.max(...values, 1);
  const last = Math.max(points.length - 1, 1);
  const path = points.map((point, index) => `${index === 0 ? "M" : "L"}${(index / last) * 112},${30 - (point.value / max) * 28}`).join(" ");
  return (
    <svg role="img" aria-label="Leads over 12 weeks" viewBox="0 0 112 32" className="h-8 w-28 text-ks-purple">
      <path d={path} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function overviewColumns(
  stage: OverviewStage,
  packScanners: number | null,
  qrFailed: boolean,
  route: { area: string; module: string },
): ColumnDef<OverviewFunnelRow, unknown>[] {
  const column = (key: OverviewTile["key"], header: string): ColumnDef<OverviewFunnelRow, unknown> => ({
    id: key,
    header,
    cell: ({ row }) => {
      const replacement = key === "leads" && row.original.key === "precision-qr"
        ? { value: qrFailed ? null : packScanners, reason: qrFailed ? "The QR service did not answer" : row.original.leads.needs_data?.reason ?? null }
        : undefined;
      return cellValue(key, row.original[key], replacement);
    },
    ...numeric,
  });
  const columns: ColumnDef<OverviewFunnelRow, unknown>[] = [
    { id: "funnel", header: "Funnel", accessorFn: (row) => row.label },
    { id: "stage", header: "Stage", accessorFn: (row) => row.stage === "pre" ? "Pre-purchase" : "Post-purchase" },
    column("leads", "Leads"),
    column("new_app_users", "New app users"),
    column("lead_to_app", "Lead to app"),
  ];
  if (stage !== "post") columns.push(column("results_given", "Results given"), column("order_made", "Order made"));
  if (stage !== "pre") columns.push(column("active_users", "Active users"), column("stickiness", "Stickiness"), column("orders_per_app_user", "Orders per app user"));
  columns.push(
    { id: "trend", header: "Leads, 12 weeks", cell: ({ row }) => <Sparkline points={row.original.leads_trend} /> },
    {
      id: "open",
      header: "",
      cell: ({ row }) => row.original.other ? null : (
        <Link to="/app/$area/$module/$tab" params={{ ...route, tab: "overview" }} search={(previous: Record<string, unknown>) => previous as never} data-nonmutating className="font-medium text-ks-purple underline-offset-4 hover:underline">
          Open in Funnels
        </Link>
      ),
    },
  );
  return columns;
}

export function SummaryTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { area?: string; module?: string };
  const state = overviewUrlState(search);
  const dates = overviewDates(state.days, new Date());
  const query = { ...dates, funnel: state.funnel };
  const overview = useQuery({
    queryKey: statsOverviewKey(FUNNEL_SLUG, query),
    queryFn: () => getFunnelOverview(FUNNEL_SLUG, query),
  });
  const qr = useQuery({
    queryKey: qrCodesKey(state.days),
    queryFn: () => getCodes(state.days),
    enabled: state.stage !== "pre",
  });
  const changeState = (next: OverviewUrlState) => {
    void navigate({ to: ".", search: { ...search, ...overviewSearch(next) } as never, replace: true });
  };
  const data = overview.data?.data;
  const packScanners = packQrScanners(qr.data?.data.codes);
  const route = { area: params.area ?? "customer_acquisition", module: params.module ?? "landing_pages" };
  const columns = overviewColumns(state.stage, packScanners, qr.isError, route);

  return (
    <div className="flex w-full min-w-0 flex-col gap-6" aria-label={`${areaName} conversion overview`}>
      <OverviewControls state={state} onChange={changeState} />

      {overview.isError ? (
        <Alert>
          <AlertTitle>Overview figures unavailable.</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-2">
            <span>The funnel stats service did not answer. Nothing was changed. Try again shortly.</span>
            <Button data-nonmutating variant="outline" size="sm" onClick={() => void overview.refetch()}>Try again</Button>
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {state.stage !== "post" && <TileSection title="Pre-purchase" tiles={data?.stages.pre.tiles} packScanners={packScanners} qrFailed={qr.isError} qrPending={qr.isPending} route={route} />}
          {state.stage !== "pre" && <TileSection title="Post-purchase" tiles={data?.stages.post.tiles} packScanners={packScanners} qrFailed={qr.isError} qrPending={qr.isPending} route={route} />}

          <section className="flex min-w-0 flex-col gap-3" aria-label="By funnel">
            <h2 className="text-base font-semibold text-ks-ink">By funnel</h2>
            <DataTable
              columns={columns}
              rows={funnelRowsForStage(data?.funnels, state.stage)}
              pending={overview.isPending}
              page={{ pageIndex: 0, pageSize: Math.max(data?.funnels.length ?? 4, 1), rowCount: data?.funnels.length ?? 0 }}
              onPageChange={() => {}}
              emptyMessage="No funnel figures are available for this window."
              caption="Conversion overview by funnel"
              cards
              wrap
            />
          </section>

          {data && (
            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
              <p>Stickiness is DAU/MAU: people using the app on an average day, over people who used it in the last 30 days.</p>
              <p>Check-ins reach only people on a Precision plan, so they are a detail under it. People count under the funnel they first came in through.</p>
              <p>Figures as of {data.data_as_of ?? "we can't confirm this data's date"}. Each tile opens the screen that explains it. Tiles carry no band colour until these KPIs have targets.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
